import { useState } from 'react';
import { useTranslation } from 'react-i18next';
import { Info, AlertTriangle, Wrench, X } from 'lucide-react';
import { useSystemMessages } from '../hooks/useSystemMessages';
import { pickLocalized, type SystemMessage } from '@shared/schemas/releases';

const DISMISSED_KEY = '__dismissed_system_msgs';

function getDismissed(): Set<string> {
  try {
    const raw = localStorage.getItem(DISMISSED_KEY);
    return new Set(raw ? JSON.parse(raw) : []);
  } catch {
    return new Set();
  }
}

function persistDismissed(ids: Set<string>) {
  try {
    localStorage.setItem(DISMISSED_KEY, JSON.stringify([...ids]));
  } catch {
    // localStorage full/blocked — no-op, siguiente reload muestra los mismos
  }
}

const KIND_STYLES: Record<SystemMessage['kind'], { bg: string; text: string; Icon: typeof Info }> = {
  info: { bg: 'bg-blue-50 border-blue-200', text: 'text-blue-900', Icon: Info },
  warning: { bg: 'bg-amber-50 border-amber-200', text: 'text-amber-900', Icon: AlertTriangle },
  maintenance: { bg: 'bg-purple-50 border-purple-200', text: 'text-purple-900', Icon: Wrench },
};

export function SystemMessageBanner() {
  const { i18n, t } = useTranslation();
  const { data: messages } = useSystemMessages();
  const [dismissed, setDismissed] = useState<Set<string>>(getDismissed);

  const visible = (messages ?? []).filter((m) => !m.dismissible || !dismissed.has(m.id));
  if (visible.length === 0) return null;

  const dismiss = (id: string) => {
    const next = new Set(dismissed);
    next.add(id);
    setDismissed(next);
    persistDismissed(next);
  };

  return (
    <div className="space-y-1 px-3 py-2">
      {visible.map((msg) => {
        const { bg, text, Icon } = KIND_STYLES[msg.kind];
        const title = pickLocalized(msg.title, i18n.language);
        const body = pickLocalized(msg.body, i18n.language);
        return (
          <div
            key={msg.id}
            role="status"
            className={`flex items-start gap-2 rounded-lg border ${bg} ${text} px-3 py-2 text-sm`}
          >
            <Icon size={16} className="mt-0.5 shrink-0" />
            <div className="flex-1">
              {title && <p className="font-medium">{title}</p>}
              {body && <p className="text-xs opacity-90">{body}</p>}
            </div>
            {msg.dismissible && (
              <button
                type="button"
                onClick={() => dismiss(msg.id)}
                className="shrink-0 opacity-70 hover:opacity-100"
                aria-label={t('common.dismiss', { defaultValue: 'Descartar' })}
              >
                <X size={14} />
              </button>
            )}
          </div>
        );
      })}
    </div>
  );
}
